import {
  ESCALATION_DISMISS_REASONS,
  ESCALATION_DISMISS_REASON_LABELS,
  ESCALATION_OUTCOME_LABELS,
  MANAGE_ESCALATIONS_PERMISSION,
  type EscalationDetail as EscalationDetailData,
  type EscalationDismissReason,
  type EscalationEventType,
  type WriteRejection,
} from "@estelle/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Loader2, Mail, RotateCcw } from "lucide-react";
import { useId, useState, type ReactNode } from "react";

import { useDataSource } from "@/adapters/data-source";
import { can, usePermissions } from "@/auth/permissions";
import { LoadFailed } from "@/components/shell/EmptyState";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { reportingNow } from "@/lib/reportClock";
import { CommentThread } from "@/modules/comments/CommentThread";
import { EmailCourierDialog } from "@/modules/orders/screens/EscalationEmail";
import { money } from "@/modules/orders/format";
import { OrderCancelAction } from "@/modules/orders/screens/OrderCancelAction";
import {
  KindBadge,
  SentBefore,
  StatusBadge,
} from "@/modules/orders/screens/EscalationParts";
import { daysLabel } from "@/modules/orders/escalationActions";
import { exactIndiaTime, timeAgo } from "@/modules/orders/tracking";

/**
 * One escalation, opened over the list (#702): the facts, what has been done
 * about it, the scan timeline, every send, the history and the thread.
 *
 * The facts are about the parcel and the order number. Never the customer's
 * name, phone or address: the API does not send them, and nothing here asks.
 *
 * Cancel order is the order's own write-through action, so the escalation
 * closes as "order cancelled" only after Shopify has accepted it. Stop
 * tracking needs a reason and a comment and moves the row to Dismissed;
 * Reopen puts a closed row back to where it was.
 */

const EVENT_LABELS: Record<EscalationEventType, string> = {
  opened: "Opened",
  level_raised: "Level raised",
  sent: "Sent to the courier",
  resolved: "Resolved",
  dismissed: "Stopped tracking",
  reopened: "Reopened",
};

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold">{title}</h3>
      {children}
    </section>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="text-sm wrap-anywhere">{children}</dd>
    </div>
  );
}

function when(value: string): ReactNode {
  return (
    <span title={exactIndiaTime(value)}>{timeAgo(value, reportingNow())}</span>
  );
}

/** Stop tracking: a reason and a comment, both required, and the refusal's words if the server says no. */
function StopTrackingDialog({
  escalationId,
  onClose,
}: {
  escalationId: string;
  onClose: () => void;
}) {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const commentId = useId();
  const [reason, setReason] = useState<EscalationDismissReason | null>(null);
  const [comment, setComment] = useState("");
  const [rejection, setRejection] = useState<WriteRejection | null>(null);

  const mutation = useMutation({
    mutationFn: () => {
      if (reason === null) throw new Error("no reason");
      return data.escalations.dismiss(escalationId, { reason, comment });
    },
    onSuccess: (result) => {
      if (result.ok) {
        void queryClient.invalidateQueries({ queryKey: ["escalations"] });
        void queryClient.invalidateQueries({ queryKey: ["comments"] });
        onClose();
      } else {
        setRejection(result.rejection);
      }
    },
  });

  const fields = rejection?.kind === "invalid" ? rejection.fields : undefined;
  const ready = reason !== null && comment.trim() !== "";

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !mutation.isPending) onClose();
      }}
    >
      <DialogContent
        title="Stop tracking"
        description="It leaves the count and the emails."
      >
        <form
          className="space-y-4 p-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (ready) mutation.mutate();
          }}
        >
          <div className="space-y-1.5">
            <Label>Reason</Label>
            <Select
              value={reason ?? undefined}
              onValueChange={(value) => {
                setReason(value as EscalationDismissReason);
              }}
            >
              <SelectTrigger
                className="min-h-11 w-full"
                aria-label="Reason"
                aria-invalid={fields?.reason ? true : undefined}
              >
                <SelectValue placeholder="Choose a reason" />
              </SelectTrigger>
              <SelectContent>
                {ESCALATION_DISMISS_REASONS.map((value) => (
                  <SelectItem key={value} value={value}>
                    {ESCALATION_DISMISS_REASON_LABELS[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fields?.reason ? (
              <p className="text-destructive text-xs">{fields.reason}</p>
            ) : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={commentId}>Comment (required)</Label>
            <Textarea
              id={commentId}
              value={comment}
              rows={3}
              maxLength={1000}
              placeholder="Say why, in a sentence. It is added to the comments."
              onChange={(event) => {
                setComment(event.target.value);
              }}
              aria-invalid={fields?.comment ? true : undefined}
            />
            {fields?.comment ? (
              <p className="text-destructive text-xs">{fields.comment}</p>
            ) : null}
          </div>
          {rejection && !fields ? (
            <p role="alert" className="text-destructive text-sm">
              {rejection.message}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              disabled={mutation.isPending}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="min-h-11"
              disabled={!ready || mutation.isPending}
            >
              {mutation.isPending ? (
                <Loader2 className="animate-spin" aria-hidden />
              ) : null}
              Stop tracking
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function EscalationDetail({ escalationId }: { escalationId: string }) {
  const data = useDataSource();
  const permissions = usePermissions();
  const queryClient = useQueryClient();
  const [stopping, setStopping] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [emailNote, setEmailNote] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const canManage = can(permissions, MANAGE_ESCALATIONS_PERMISSION);

  const query = useQuery({
    queryKey: ["escalations", "detail", escalationId],
    queryFn: () => data.escalations.get(escalationId),
  });

  const reopen = useMutation({
    mutationFn: () => data.escalations.reopen(escalationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["escalations"] });
    },
  });

  if (query.isPending) {
    return (
      <div className="text-muted-foreground flex items-center gap-2 p-4 text-sm">
        <Loader2 className="size-4 animate-spin" aria-hidden />
        Loading
      </div>
    );
  }
  if (query.isError) {
    return (
      <LoadFailed
        message={query.error.message}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }
  const row: EscalationDetailData | null = query.data;
  if (row === null) {
    return (
      <p className="text-muted-foreground p-4 text-sm">
        That escalation is not there any more.
      </p>
    );
  }

  const open = row.status === "new" || row.status === "sent";
  const rejection =
    reopen.data && !reopen.data.ok ? reopen.data.rejection : null;

  return (
    <div className="space-y-6 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <KindBadge kind={row.kind} level={row.level} />
        <StatusBadge status={row.status} />
        {row.outcome ? (
          <span className="text-muted-foreground text-sm">
            {ESCALATION_OUTCOME_LABELS[row.outcome]}
          </span>
        ) : null}
        {row.dismissedReason ? (
          <span className="text-muted-foreground text-sm">
            {ESCALATION_DISMISS_REASON_LABELS[row.dismissedReason]}
            {row.dismissedByName ? `, by ${row.dismissedByName}` : ""}
          </span>
        ) : null}
      </div>
      <SentBefore row={row} />

      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
        <Fact label="Order">{row.orderNumber}</Fact>
        <Fact label="AWB">
          <span className="inline-flex items-center gap-1 tabular-nums">
            {row.awb ?? "—"}
            {row.awb ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8"
                aria-label={copied ? "AWB copied" : "Copy AWB"}
                onClick={() => {
                  void navigator.clipboard.writeText(row.awb ?? "").then(() => {
                    setCopied(true);
                  });
                }}
              >
                <Copy aria-hidden className="size-3.5" />
              </Button>
            ) : null}
          </span>
        </Fact>
        <Fact label="Courier">{row.courier ?? "—"}</Fact>
        <Fact label="Open for">
          <span title={exactIndiaTime(row.openedAt)}>
            {daysLabel(row.daysOpen)}
          </span>
        </Fact>
        <Fact label="Last scan">
          {row.lastScanAt ? when(row.lastScanAt) : "—"}
        </Fact>
        <Fact label="Attempts">{row.attempts}</Fact>
        <Fact label="Value">
          {money(row.orderValue)}
          {row.payment ? ` · ${row.payment === "cod" ? "COD" : "Prepaid"}` : ""}
        </Fact>
        <Fact label="Last sent">
          {row.lastSentAt ? when(row.lastSentAt) : "Not sent yet"}
        </Fact>
      </dl>

      {canManage || (open && row.orderCancellable) ? (
        <div className="flex flex-wrap gap-2">
          {canManage && row.status === "new" ? (
            <Button
              type="button"
              className="min-h-11"
              onClick={() => {
                setEmailNote(null);
                setEmailing(true);
              }}
            >
              <Mail aria-hidden />
              Email courier
            </Button>
          ) : null}
          {canManage && open ? (
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              onClick={() => {
                setStopping(true);
              }}
            >
              Stop tracking
            </Button>
          ) : null}
          {canManage && !open ? (
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              disabled={reopen.isPending}
              onClick={() => {
                reopen.mutate();
              }}
            >
              {reopen.isPending ? (
                <Loader2 className="animate-spin" aria-hidden />
              ) : (
                <RotateCcw aria-hidden />
              )}
              Reopen
            </Button>
          ) : null}
          {open &&
          row.orderCancellable &&
          can(permissions, "orders.detail.cancel") ? (
            <OrderCancelAction
              orderId={row.orderId}
              orderNumber={row.orderNumber}
              channelUpdatedAt={row.orderChannelUpdatedAt}
              cancelledAt={null}
            />
          ) : null}
        </div>
      ) : null}
      {emailNote ? (
        <p role="status" className="text-muted-foreground text-sm">
          {emailNote}
        </p>
      ) : null}
      {emailing ? (
        <EmailCourierDialog
          ids={[row.id]}
          onClose={() => {
            setEmailing(false);
          }}
          onDone={(message) => {
            setEmailing(false);
            setEmailNote(message);
          }}
        />
      ) : null}
      {rejection ? (
        <p role="alert" className="text-destructive text-sm">
          {rejection.message}
        </p>
      ) : null}

      <Section title="Scans">
        {row.scans.length === 0 ? (
          <p className="text-muted-foreground text-sm">No scans yet.</p>
        ) : (
          <ol className="space-y-2">
            {row.scans.map((scan, index) => (
              <li
                key={`${scan.happenedAt}-${String(index)}`}
                className="border-l-2 pl-3 text-sm"
              >
                <span className="font-medium">{scan.status || "Scan"}</span>
                {scan.location ? (
                  <span className="text-muted-foreground">
                    {" "}
                    · {scan.location}
                  </span>
                ) : null}
                <span className="text-muted-foreground block text-xs">
                  {exactIndiaTime(scan.happenedAt)}
                </span>
              </li>
            ))}
          </ol>
        )}
      </Section>

      <Section title="Sent to the courier">
        {row.sends.length === 0 ? (
          <p className="text-muted-foreground text-sm">Not sent yet.</p>
        ) : (
          <ul className="space-y-1.5 text-sm">
            {row.sends.map((send, index) => (
              <li key={`${send.sentAt}-${String(index)}`}>
                {exactIndiaTime(send.sentAt)}
                {send.level ? ` · level ${String(send.level)}` : ""}
                {send.sentByName ? ` · ${send.sentByName}` : ""}
                {send.method === "marked" ? " · marked as sent" : " · emailed"}
                {send.outcome === "failed" ? (
                  <span className="text-destructive">
                    {" "}
                    · failed{send.reason ? `: ${send.reason}` : ""}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="History">
        <ul className="space-y-1.5 text-sm">
          {row.history.map((entry, index) => (
            <li key={`${entry.at}-${String(index)}`}>
              <span className="font-medium">{EVENT_LABELS[entry.type]}</span>
              {entry.level ? ` · level ${String(entry.level)}` : ""}
              {entry.userName ? ` · ${entry.userName}` : ""}
              <span className="text-muted-foreground block text-xs">
                {exactIndiaTime(entry.at)}
              </span>
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Comments">
        <CommentThread entityType="escalation" entityId={row.id} />
      </Section>

      {stopping ? (
        <StopTrackingDialog
          escalationId={row.id}
          onClose={() => {
            setStopping(false);
          }}
        />
      ) : null}
    </div>
  );
}
